import { Crown, Sparkles, FileDown } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, Callout, RoleBadge, pathFor, StatusBadge,
  Diagram, MockTabs, MockTable, MockButton, MockRow,
} from "../shared";

export default function PagesMdMeetingsSection() {
  return (
    <div data-testid="guide-pages-md-meetings">
      <PageHeading
        icon={Crown}
        title="MD Meetings"
        description="The SDF MD's review hub. Everything waiting on MD action sits here in one place, with quick approve, send-back, and a printable agenda — plus a separate tab for items the PMO still needs to send upward."
      />

      <Callout variant="info" title="In plain words">
        This is the <span className="text-white">MD's inbox for decisions</span>. One tab is the pile to review and approve; the other tab is the pile that's been approved and now needs sending upward. You can clear several at once instead of one by one.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>When you're the SDF MD clearing the day's review queue.</li>
          <li>When the PMO needs to see which approved items still have to go upward.</li>
          <li>To print today's queue as a meeting agenda.</li>
        </ul>
      </Section>

      <Section title="A picture of the page">
        <Diagram
          frameLabel="MD Meetings"
          legend={[
            <>The <span className="text-white">Review Queue</span> tab lists every item in <StatusBadge status="WITH_SDF_MD_REVIEW" />, oldest first.</>,
            <>The <span className="text-white">Awaiting Dispatch</span> tab lists approved above-MD items the PMO still needs to send upward, oldest-approved first.</>,
            <>Tick the <span className="text-white">checkboxes</span> to select items, then use the bulk <span className="text-white">Approve &amp; Close</span> button. Each row also has its own <span className="text-white">Return</span> button.</>,
            <>The <span className="inline-flex items-center gap-1"><FileDown className="w-3 h-3 text-cyan-300" /> Download PDF agenda</span> button saves the current tab's queue as a printable landscape page.</>,
          ]}
        >
          <MockTabs
            tabs={[
              { label: "Review Queue", active: true, marker: 1 },
              { label: "Awaiting Dispatch", marker: 2 },
            ]}
          />
          <MockTable
            columns={["✓", "Item", "Received", "Priority", ""]}
            rows={[
              ["☑", "Board resolution 14", "2 days ago", "High", <MockButton label="Return" variant="ghost" />],
              ["☐", "ExCom paper", "1 day ago", "Medium", <MockButton label="Return" variant="ghost" />],
            ]}
          />
          <MockRow>
            <MockButton label="Approve & Close" marker={3} />
            <MockButton label="Download PDF agenda" marker={4} variant="ghost" />
          </MockRow>
        </Diagram>
        <Steps
          steps={[
            <>Stay on the <span className="text-white">Review Queue</span> tab to work through items waiting on you. Use the filter chips (due bucket, priority, "show only mine") to focus.</>,
            <>Switch to <span className="text-white">Awaiting Dispatch</span> <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-300" /></span> to see approved items still to be sent upward — each shows a held-time chip (green / amber / red).</>,
            <>Tick the items you want, then press <span className="text-white">Approve &amp; Close</span> to clear them in one go. To send one back, press its <span className="text-white">Return</span> button with a clear reason.</>,
            <>Press <span className="text-white">Download PDF agenda</span> any time to print the current tab.</>,
          ]}
        />
      </Section>

      <Section title="Who can do what">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <RoleBadge role="md_admin" />
            <span className="text-xs text-white/55">View both tabs and download the PDF (read-only).</span>
          </div>
          <div className="flex items-center gap-2">
            <RoleBadge role="super_admin" />
            <span className="text-xs text-white/55">Full access — approve, sign off, return, mark dispatched.</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 text-xs border border-amber-500/30">SDF MD principal</span>
            <span className="text-xs text-white/55">Same as Super Admin for this page only. Identified by team-member binding plus team-member name = "SDF MD".</span>
          </div>
        </div>
      </Section>

      <Callout variant="info" title="Step-by-step walkthroughs">
        See <Link href={pathFor("forms/approve-close")} className="text-cyan-300 hover:underline">Approve &amp; Close</Link>,{" "}
        <Link href={pathFor("forms/return")} className="text-cyan-300 hover:underline">Return for Amendments</Link>, and{" "}
        <Link href={pathFor("forms/mark-dispatched")} className="text-cyan-300 hover:underline">Mark Dispatched</Link> for detailed instructions on each action.
      </Callout>
    </div>
  );
}
